import { ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { DateFilterEnum, PeriodFilterEnum } from '@/ui';
import { ReportsFilterContext } from './context';
import { useCompanyContext } from '@/companies/providers';
import { useTimeSettings } from '@/common/hooks';
import {
    ReportDateRange,
    getDateFilterFromQueryString,
    getDateFilterQueryString,
    getDateRangeFromPreset
} from '../../util/report-date-filter';
import {
    getAllowedPeriods,
    getPeriodFromQueryString,
    getPeriodQueryString
} from '../../util/report-period';

type ReportsFilterProviderProps = {
    readonly children?: ReactNode;
};

export function ReportsFilterProvider(
    props: ReportsFilterProviderProps
): ReactElement {
    const { children } = props;

    const {
        company: { createdAt }
    } = useCompanyContext();
    const { timezone } = useTimeSettings();

    const createdAtDateUTC = useMemo(() => {
        return new Date(createdAt);
    }, [createdAt]);

    const [searchParams, setSearchParams] = useSearchParams();

    const [filter, __setFilter] = useState<{
        date: {
            preset: DateFilterEnum;
            range: ReportDateRange;
        };
        period: PeriodFilterEnum;
    }>(() => {
        return {
            date: getDateFilterFromQueryString({
                value: searchParams.get('t'),
                earliestDate: createdAtDateUTC,
                timezone
            }),
            period: getPeriodFromQueryString(searchParams.get('p'))
        };
    });

    const setFilter = useCallback(
        (filter: {
            date?: {
                preset: DateFilterEnum;
                range: ReportDateRange | null;
            };
            period?: PeriodFilterEnum;
        }) => {
            let date: {
                preset: DateFilterEnum;
                range: ReportDateRange;
            };
            let period: PeriodFilterEnum;

            if (filter.date) {
                date = {
                    preset: filter.date.preset,
                    range: getDateRangeFromPreset(
                        filter.date.preset,
                        filter.date.range,
                        createdAtDateUTC,
                        timezone
                    )
                };
            } else {
                date = getDateFilterFromQueryString({
                    value: searchParams.get('t'),
                    earliestDate: createdAtDateUTC,
                    timezone
                });
            }

            if (filter.period) {
                period = filter.period;
            } else {
                period = getPeriodFromQueryString(searchParams.get('p'));
            }

            const allowedPeriods = getAllowedPeriods(date.range);
            if (!allowedPeriods.includes(period)) {
                period = allowedPeriods[0]; // Default to the first allowed
            }

            __setFilter({
                date,
                period
            });
            setSearchParams((prev) => {
                prev.delete('t');
                prev.append(
                    't',
                    getDateFilterQueryString(timezone, date.preset, date.range)
                );
                prev.delete('p');
                prev.append('p', getPeriodQueryString(period));
                return prev;
            });
        },
        [searchParams, createdAtDateUTC, setSearchParams, timezone]
    );

    const allowedPeriods = useMemo(() => {
        return getAllowedPeriods(filter.date.range);
    }, [filter.date.range]);

    return (
        <ReportsFilterContext.Provider
            value={{
                preset: filter.date.preset,
                range: filter.date.range,
                timezone,
                period: filter.period,
                allowedPeriods,
                setFilter
            }}
        >
            {children}
        </ReportsFilterContext.Provider>
    );
}
